import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Users, AlertTriangle, ArrowRight, Layers, Receipt } from "lucide-react";
import FinanceLayout from "@/components/FinanceLayout";
import MonthPicker from "@/components/MonthPicker";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api } from "@/lib/api";
import { fmtTL } from "@/lib/finance";
import { NEEDS } from "@/lib/constants";

const needLabel = (v) => NEEDS.find((n) => n.value === v)?.label || v;

const StatCard = ({ Icon, label, value, delta, tone, testId, action }) => {
  const tones = {
    green: "bg-emerald-50 text-emerald-700",
    up: "bg-emerald-50 text-emerald-700",
    down: "bg-red-50 text-red-500",
    warn: "bg-orange-50 text-orange-600",
    sky: "bg-sky-50 text-sky-700",
  };
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-6" data-testid={testId}>
      <div className={`w-11 h-11 rounded-full ${tones[tone]} flex items-center justify-center mb-5`}><Icon size={20} /></div>
      <div className="text-sm text-slate-500 mb-2">{label}</div>
      <div className="text-3xl font-bold tracking-tight text-slate-900">{value}</div>
      {delta && <div className="text-xs text-slate-500 mt-2">{delta}</div>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
};

const BreakdownCard = ({ Icon, label, tone, entries, empty, testId }) => (
  <div className="rounded-2xl border border-slate-100 bg-white p-6" data-testid={testId}>
    <div className={`w-11 h-11 rounded-full ${tone} flex items-center justify-center mb-5`}><Icon size={20} /></div>
    <div className="text-sm text-slate-500 mb-3">{label}</div>
    {entries.length === 0 ? (
      <div className="text-xs text-slate-400">{empty}</div>
    ) : (
      <div className="space-y-1.5 max-h-[180px] overflow-y-auto pr-1">
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-xs">
            <span className="text-slate-600 truncate pr-2">{k}</span>
            <span className="font-semibold text-slate-900 tabular-nums whitespace-nowrap">{fmtTL(v)}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default function FinanceDashboard() {
  const { workspace } = useWorkspace();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [{ data: d }, { data: s }] = await Promise.all([
        api.get("/finance/dashboard", { params: { workspace, year, month } }),
        api.get("/finance/monthly-summary", { params: { workspace, year, month } }),
      ]);
      if (alive) { setData(d); setSummary(s); }
    })();
    return () => { alive = false; };
  }, [workspace, year, month]);

  const deltaText = (pct) => pct == null ? "—" : `${pct > 0 ? "+" : ""}${pct}% bu ay`;

  const kapsamEntries = summary?.kapsam_ciro
    ? Object.entries(summary.kapsam_ciro).map(([k, v]) => [needLabel(k), v]).sort((a, b) => b[1] - a[1])
    : [];

  const giderEntries = (() => {
    if (!summary) return [];
    const map = { ...(summary.is_bazli_breakdown || {}), ...(summary.sabit_isletme_breakdown || {}), ...(summary.diger_breakdown || {}) };
    if (summary.personel_gideri) map["Personel Gideri"] = summary.personel_gideri;
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  })();

  return (
    <FinanceLayout>
      <div className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Genel Özet</h1>
          <p className="text-sm text-slate-500 mt-2">İşletmenizin genel durumunu buradan takip edebilirsiniz.</p>
        </div>
        <MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} testId="dashboard-monthpicker" />
      </div>

      <div className="grid grid-cols-4 gap-5 mb-6">
        <StatCard Icon={Users} label="Aktif Sürekli Müşteriler" value={data?.aktif_musteri ?? "—"} delta="Son ay verisi" tone="green" testId="stat-active-customers" />
        <StatCard Icon={TrendingUp} label="Aylık Ciro" value={fmtTL(data?.ciro)} delta={deltaText(data?.ciro_delta_pct)} tone="up" testId="stat-ciro" />
        <StatCard Icon={TrendingDown} label="Aylık Gider" value={fmtTL(data?.gider)} delta={deltaText(data?.gider_delta_pct)} tone="down" testId="stat-gider" />
        <StatCard Icon={AlertTriangle} label="Bekleyen Ödemeler" value={fmtTL(data?.bekleyen)} tone="warn" testId="stat-pending"
          action={<Link to="/finance/pending-payments" data-testid="pending-detail-link" className="text-sm text-orange-600 hover:text-orange-700 font-medium inline-flex items-center gap-1">Detayları Gör <ArrowRight size={13} /></Link>} />
      </div>

      <div className="grid grid-cols-3 gap-5">
        <StatCard Icon={Receipt} label="Tek Seferlik İşler" value={`${summary?.tek_seferlik_count || 0} iş`} delta={`Ciro: ${fmtTL(summary?.tek_seferlik_ciro || 0)}`} tone="sky" testId="stat-ot-jobs" />
        <BreakdownCard Icon={Layers} label="Çalışma Kapsamına Göre Cirolar" tone="bg-emerald-50 text-emerald-700" entries={kapsamEntries} empty="Bu ay ciro verisi yok." testId="stat-kapsam" />
        <BreakdownCard Icon={TrendingDown} label="Gider Kalemleri" tone="bg-red-50 text-red-500" entries={giderEntries} empty="Bu ay gider yok." testId="stat-giderler" />
      </div>
    </FinanceLayout>
  );
}
